import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Clock, Award, Phone, Mail } from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1730584475256-9055b74420a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBmYWJyaWNhdGlvbiUyMHdlbGRpbmclMjBzcGFya3MlMjBmYWN0b3J5fGVufDB8fHx8MTc3MjI4NjY1Mnww&ixlib=rb-4.1.0&q=85";

const GALLERY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/zmbjaipw_WhatsApp-Image-2025-09-08-at-5.00.17-PM.jpeg",
  "https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/il74h3p0_1653486171198.jpeg",
  "https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/u8eviqwt_IMG_20200221_125112-scaled-1.jpg",
  "https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/kwszlrgn_architectural-structural-steelwork-compressor.jpg",
  "https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/jmyf48r0_WhatsApp-Image-2025-07-25-at-6.10.30-AM-1.jpeg"
];

export const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG} 
            alt="Steel fabrication" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-steel-dark/85 to-steel-dark/50" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in">
              <p className="font-heading font-bold text-white uppercase tracking-widest text-sm">
                Welcome to
              </p>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl text-white uppercase tracking-tight leading-tight mb-2 animate-slide-in-bottom drop-shadow-lg">
              NJ Steel Fabrications –
            </h1>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-5xl text-yellow-400 uppercase tracking-tight mb-6 animate-slide-in-bottom drop-shadow-lg" style={{ animationDelay: '100ms' }}>
              Where Vision Gets Built
            </h2>
            <p className="font-body text-white/90 text-base sm:text-lg leading-relaxed mb-8 animate-fade-in max-w-xl" style={{ animationDelay: '200ms' }}>
              Choose NJ Steel Fabrications for strength, quality, and a team you can trust. We design, fabricate, and install structural steel that keeps projects moving—safely, on time, and to spec.
            </p>
            
            {/* Contact Info & CTA */}
            <div className="flex flex-col gap-6 animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                <a 
                  href="tel:0424456596" 
                  data-testid="hero-phone"
                  className="flex items-center gap-3 text-white hover:text-yellow-400 transition-colors group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 group-hover:bg-white transition-colors">
                    <Phone className="w-5 h-5 text-steel-dark" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-white/70 uppercase tracking-wide">Call Us</p>
                    <p className="font-heading font-bold text-lg">0424 456 596</p>
                  </div>
                </a>
                <a 
                  href="mailto:njsteelfab@gmail.com" 
                  data-testid="hero-email"
                  className="flex items-center gap-3 text-white hover:text-yellow-400 transition-colors group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 group-hover:bg-white transition-colors">
                    <Mail className="w-5 h-5 text-steel-dark" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-white/70 uppercase tracking-wide">Email Us</p>
                    <p className="font-heading font-bold text-lg">njsteelfab@gmail.com</p>
                  </div>
                </a>
              </div>
              
              <Link to="/services">
                <Button 
                  data-testid="hero-cta-services"
                  className="font-heading font-bold uppercase tracking-widest text-sm rounded-none px-8 py-6 bg-yellow-400 text-steel-dark hover:bg-white group"
                >
                  View Services
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        data-testid="stats-section"
        className="bg-white py-20 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-steel-dark uppercase tracking-tight mb-4">
                A Decade of Steelwork Excellence
              </h2>
              <p className="font-body text-steel-gray text-base leading-relaxed">
                From shop drawings and cutting to on-site erection, our certified team handles the full workflow. We coordinate with GCs, engineers, and other trades, keeping timelines tight and costs predictable.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div data-testid="stat-experience" className="text-center p-8 bg-background border border-slate-200 hover:border-primary transition-colors">
                <p className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl text-primary tracking-tight">
                  13
                </p>
                <p className="font-heading font-bold text-lg uppercase tracking-wide text-steel-dark mt-2">
                  Years Experience
                </p>
              </div>
              <div data-testid="stat-projects" className="text-center p-8 bg-background border border-slate-200 hover:border-primary transition-colors">
                <p className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl text-primary tracking-tight">
                  480
                </p>
                <p className="font-heading font-bold text-lg uppercase tracking-wide text-steel-dark mt-2">
                  Projects Completed
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        data-testid="about-section"
        className="py-24 bg-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <p className="font-body text-primary font-medium tracking-wide mb-4">
                About Us
              </p>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-steel-dark uppercase tracking-tight mb-6">
                Quality Steel & Guaranteed Service
              </h2>
              <p className="font-body text-steel-gray text-base leading-relaxed mb-6">
                At NJ Steel Fabrications, we specialise in delivering quality Steel and Guaranteed service across Melbourne and parts of Victoria. With years of industry experience and a commitment to precision and innovation, we bring your structural and architectural steel projects to life.
              </p>
              <p className="font-body text-steel-gray text-base leading-relaxed mb-8">
                Our expert team can handle residential, commercial, and industrial projects, offering design, fabrication, on-site installation and Rectification jobs with a focus on quality, quick turnaround, and personalized service. Whether it's a custom stair, a mezzanine, or a multi-storey frame, NJ Steel builds it right and stands behind every weld.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-sm uppercase tracking-wide text-steel-dark">Quality</p>
                    <p className="font-body text-steel-gray text-sm">AS1554 Certified</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-sm uppercase tracking-wide text-steel-dark">On Time</p>
                    <p className="font-body text-steel-gray text-sm">Quick Turnaround</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10">
                    <Award className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-sm uppercase tracking-wide text-steel-dark">Expert</p>
                    <p className="font-body text-steel-gray text-sm">Full Service Team</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <img 
                src={GALLERY_IMAGES[1]} 
                alt="Steel structure" 
                className="w-full h-[400px] object-cover border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section 
        data-testid="gallery-section"
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-primary font-medium tracking-wide mb-4">
              Our Work
            </p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-steel-dark uppercase tracking-tight">
              Project Gallery
            </h2>
          </div>

          {/* Bento Grid Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Large Image */}
            <div className="col-span-2 row-span-2">
              <img 
                src={GALLERY_IMAGES[3]} 
                alt="Steel construction project" 
                data-testid="gallery-image-1"
                className="w-full h-full min-h-[300px] md:min-h-[400px] object-cover border border-slate-200 hover:border-primary transition-colors cursor-pointer"
              />
            </div>
            {/* Small Images */}
            <div className="col-span-1">
              <img 
                src={GALLERY_IMAGES[0]} 
                alt="Steel fabrication project" 
                data-testid="gallery-image-2"
                className="w-full h-full min-h-[150px] md:min-h-[195px] object-cover border border-slate-200 hover:border-primary transition-colors cursor-pointer"
              />
            </div>
            <div className="col-span-1">
              <img 
                src={GALLERY_IMAGES[1]} 
                alt="Steel frame project" 
                data-testid="gallery-image-3"
                className="w-full h-full min-h-[150px] md:min-h-[195px] object-cover border border-slate-200 hover:border-primary transition-colors cursor-pointer"
              />
            </div>
            <div className="col-span-1">
              <img 
                src={GALLERY_IMAGES[4]} 
                alt="Curved steel project" 
                data-testid="gallery-image-4"
                className="w-full h-full min-h-[150px] md:min-h-[195px] object-cover border border-slate-200 hover:border-primary transition-colors cursor-pointer"
              />
            </div>
            <div className="col-span-1">
              <img 
                src={GALLERY_IMAGES[2]} 
                alt="Crane service project" 
                data-testid="gallery-image-5"
                className="w-full h-full min-h-[150px] md:min-h-[195px] object-cover border border-slate-200 hover:border-primary transition-colors cursor-pointer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Animation */}
      <section 
        data-testid="cta-section"
        className="py-20 bg-gradient-to-r from-primary via-steel-dark to-primary relative overflow-hidden"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-400/20 rounded-full animate-pulse" />
          <div className="absolute top-1/2 -right-8 w-32 h-32 bg-yellow-400/10 rounded-full animate-bounce" style={{ animationDuration: '3s' }} />
          <div className="absolute bottom-4 left-1/4 w-16 h-16 bg-white/10 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                Ready to Start Your Project?
              </h2>
              <p className="font-body text-white/80 text-base lg:text-lg max-w-xl">
                Contact us today for a free consultation and quote. We're ready to bring your steel vision to life.
              </p>
            </div>
            
            {/* Animated Worker Character */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {/* Worker SVG Animation */}
                <div className="w-32 h-32 lg:w-40 lg:h-40 relative animate-bounce" style={{ animationDuration: '2s' }}>
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {/* Hard Hat */}
                    <ellipse cx="50" cy="25" rx="25" ry="12" fill="#FFD700" />
                    <rect x="25" y="20" width="50" height="8" fill="#FFD700" />
                    <rect x="30" y="15" width="40" height="8" rx="4" fill="#FFA500" />
                    
                    {/* Face */}
                    <circle cx="50" cy="40" r="15" fill="#FDBF6F" />
                    
                    {/* Eyes */}
                    <circle cx="45" cy="38" r="2" fill="#333" className="animate-pulse" />
                    <circle cx="55" cy="38" r="2" fill="#333" className="animate-pulse" />
                    
                    {/* Smile */}
                    <path d="M 43 45 Q 50 50 57 45" stroke="#333" strokeWidth="2" fill="none" />
                    
                    {/* Body - Safety Vest */}
                    <rect x="35" y="55" width="30" height="35" fill="#FF6B00" rx="3" />
                    <rect x="38" y="55" width="3" height="35" fill="#FFFF00" />
                    <rect x="59" y="55" width="3" height="35" fill="#FFFF00" />
                    <rect x="35" y="70" width="30" height="3" fill="#FFFF00" />
                    
                    {/* Arms */}
                    <rect x="20" y="58" width="15" height="8" fill="#FDBF6F" rx="4" />
                    <rect x="65" y="58" width="15" height="8" fill="#FDBF6F" rx="4" />
                    
                    {/* Gloves */}
                    <circle cx="18" cy="62" r="5" fill="#FFD700" />
                    <circle cx="82" cy="62" r="5" fill="#FFD700" />
                    
                    {/* Tool in hand - Wrench */}
                    <rect x="80" y="55" width="4" height="20" fill="#666" className="origin-bottom animate-pulse" style={{ animationDuration: '1s' }} />
                  </svg>
                </div>
                
                {/* Sparks Animation */}
                <div className="absolute -top-2 -right-2">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping" />
                </div>
                <div className="absolute top-4 -left-4">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                </div>
                <div className="absolute bottom-8 -right-6">
                  <div className="w-2 h-2 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '1s' }} />
                </div>
              </div>
              
              <Link to="/contact#quote-form">
                <Button 
                  data-testid="cta-contact-button"
                  className="font-heading font-bold uppercase tracking-widest text-sm rounded-none px-8 py-6 bg-yellow-400 text-steel-dark hover:bg-white border-0"
                >
                  Contact Us Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
